/// <reference path="../nativewind-augmentation.d.ts" />
// Copyright (c) Ping. All rights reserved.
//
// invite-flow Wave 2 — shared RN+NativeWind ShareLocation overlay v2.
//
// Maps the shell view-model's `shareLocation` slot to the underlying
// `ShareLocationModal` (figma-faithful surface in
// `packages/ui/src/share-location/`).
//
// Coordinates (`Location.lat` / `.lng`) live in memory only and are
// NEVER logged — Hard rule 3. The overlay forwards them through the
// host's `onShare` callback without persisting or logging.
//
// FOLLOW-UP (desktop): `navigator.geolocation` is available in web but
// not in react-native-macos. The desktop host must supply
// `onResolveCurrentLocation` via either:
//   - `react-native-geolocation-service` (community RN macOS support),
//   - a bridge to `CLLocationManager` (native Swift module).
// Until that lands, the modal still functions for manual search — the
// "Use current location" affordance is disabled when the resolver
// prop is omitted.

import type React from "react";
import {
	ShareLocationModal,
	type Location,
	type LocationSearchResult,
	type ShareLocationResult,
} from "../share-location";
import type { ChatRoomShellViewModel } from "../chat/useChatRoomShellViewModel";

export interface ShareLocationOverlayProps {
	readonly vm: ChatRoomShellViewModel;
	readonly onShare: (result: ShareLocationResult) => Promise<void> | void;
	readonly onSearch?: (
		query: string,
		signal: AbortSignal,
	) => Promise<readonly LocationSearchResult[]>;
	readonly onResolveCurrentLocation?: (
		signal: AbortSignal,
	) => Promise<Location | null>;
	readonly defaultLiveDurationSeconds?: number;
	/** Conversation name shown in the live-duration sub-modal body. */
	readonly conversationName?: string | undefined;
	/** When true, the Share button opens the live-duration sub-modal first. */
	readonly liveMode?: boolean | undefined;
}

export const ShareLocationOverlay: React.FC<ShareLocationOverlayProps> = ({
	vm,
	onShare,
	onSearch,
	onResolveCurrentLocation,
	defaultLiveDurationSeconds,
	conversationName,
	liveMode,
}) => {
	const isOpen = vm.state.activeOverlays.includes("shareLocation");

	const handleClose = (): void => {
		vm.handlers.closeOverlay("shareLocation");
	};

	const handleShare = async (result: ShareLocationResult): Promise<void> => {
		try {
			await onShare(result);
		} finally {
			vm.handlers.closeOverlay("shareLocation");
		}
	};

	return (
		<ShareLocationModal
			visible={isOpen}
			{...(defaultLiveDurationSeconds !== undefined
				? { defaultLiveDurationSeconds }
				: {})}
			{...(conversationName !== undefined ? { conversationName } : {})}
			{...(liveMode !== undefined ? { liveMode } : {})}
			callbacks={{
				onClose: handleClose,
				onShare: handleShare,
				...(onSearch !== undefined ? { onSearch } : {}),
				...(onResolveCurrentLocation !== undefined
					? { onResolveCurrentLocation }
					: {}),
			}}
		/>
	);
};
